import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { Alert, Button, Chip, Note, PageHeader, Panel, Spinner } from '../ui';

/**
 * A-35 — the translation table.
 *
 * A full screen rather than a dialog, because this is bulk work: a translator
 * sits with it for an hour and goes down the list. A modal is for one decision
 * you came to make, and it takes the page away while you are in it.
 *
 * Every row is a key from `packages/i18n` with its English source beside the
 * field. The English is the brief — a translator working from
 * `payables.dueRule.monthEnd` alone is guessing at what the sentence has to do
 * — and it doubles as the
 * placeholder, so an empty field previews exactly what the customer will see if
 * it stays empty.
 */

interface LocaleRow {
  code: string;
  name: string;
  endonym: string;
  direction: 'ltr' | 'rtl';
  isActive: boolean;
  translated: number;
  total: number;
}

interface KeyRow { key: string; english: string }

/** The first segment of the key. `shop.catalog.title` files under `shop`. */
const categoryOf = (key: string) => key.split('.')[0] ?? 'other';

export function Translations() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();

  const { data: locales, isLoading: loadingLocales } = useQuery({
    queryKey: ['locales'],
    queryFn: () => api.get<LocaleRow[]>('/admin/locales'),
  });
  const { data: keys, isLoading: loadingKeys } = useQuery({
    queryKey: ['locale-keys'],
    queryFn: () => api.get<KeyRow[]>('/admin/locales/keys'),
  });

  // The language lives in the URL, so a half-finished pass survives a reload and
  // can be handed to somebody else as a link.
  const chosen = params.get('lang') ?? locales?.[0]?.code ?? '';
  const locale = locales?.find((l) => l.code === chosen);

  const { data: stored } = useQuery({
    queryKey: ['locale-messages', chosen],
    queryFn: () => api.get<Record<string, string>>(`/admin/locales/${chosen}/messages`)
      .catch((): Record<string, string> => ({})),
    enabled: !!chosen,
  });

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<number | null>(null);

  const save = useMutation({
    mutationFn: (messages: Record<string, string>) =>
      api.put<unknown>(`/admin/locales/${chosen}/messages`, { messages }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['locales'] });
      void qc.invalidateQueries({ queryKey: ['locale-messages', chosen] });
    },
  });

  const categories = useMemo(
    () => [...new Set((keys ?? []).map((k) => categoryOf(k.key)))].sort(),
    [keys],
  );

  const held = stored ?? {};
  const valueFor = (k: string) => draft[k] ?? held[k] ?? '';

  const rows = useMemo(() => (keys ?? []).filter((k) => {
    if (category && categoryOf(k.key) !== category) return false;
    if (onlyMissing && valueFor(k.key) !== '') return false;
    if (!search) return true;
    const f = search.toLowerCase();
    return k.key.toLowerCase().includes(f)
      || k.english.toLowerCase().includes(f)
      || valueFor(k.key).toLowerCase().includes(f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [keys, category, search, onlyMissing, draft, stored]);

  const dirty = Object.keys(draft).length;

  if (loadingLocales || loadingKeys) return <Spinner />;

  if (!locales?.length) {
    return (
      <>
        <PageHeader title="Translations" />
        <Note>
          No languages yet. Add one on <b>Languages</b> first — English is the source text and is
          never translated here.
        </Note>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Translations"
        actions={
          <Button
            disabled={save.isPending || dirty === 0}
            onClick={() => {
              setError(null);
              const payload = draft;
              save.mutate(payload, {
                onSuccess: () => { setDraft({}); setSaved(Object.keys(payload).length); },
                onError: (e) => setError(e instanceof ApiError ? e.message : 'That did not work'),
              });
            }}
          >
            {dirty > 0 ? `Save ${dirty} change${dirty === 1 ? '' : 's'}` : 'Save'}
          </Button>
        }
      />

      {error ? <Alert tone="stop">{error}</Alert> : null}
      {saved !== null && dirty === 0 ? (
        <Alert tone="done">
          Saved {saved} string{saved === 1 ? '' : 's'}. They reach customers on their next page
          load — no release.
        </Alert>
      ) : null}

      <div className="mk-toolbar">
        <select
          aria-label="Language"
          value={chosen}
          onChange={(e) => { setParams({ lang: e.target.value }); setDraft({}); setSaved(null); }}
        >
          {locales.map((l) => (
            <option key={l.code} value={l.code}>{l.name} — {l.endonym}</option>
          ))}
        </select>

        <select aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>

        <input
          className="mk-fld mk-toolbar-search"
          placeholder="Search a key, the English, or the translation…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <label className="mk-toolbar-check">
          <input
            type="checkbox" checked={onlyMissing}
            onChange={(e) => setOnlyMissing(e.target.checked)}
          />
          <span>Untranslated only</span>
        </label>

        <span className="mk-spacer" />
        {locale ? (
          <Chip tone={locale.isActive ? 'done' : 'lapse'}>
            {locale.isActive ? 'Published' : 'Draft'}
          </Chip>
        ) : null}
        <span className="mk-hint mk-nowrap">{rows.length} / {keys?.length ?? 0}</span>
      </div>

      <Panel>
        <div className="mk-twrap">
          <table className="mk-t mk-dense">
            <thead>
              <tr>
                <th style={{ width: '1%' }}>Category</th>
                <th style={{ width: '1%' }}>Key</th>
                {/* Equal halves, stated rather than left to content: the English
                    strings are long and the auto layout gave them the width the
                    field being typed into needed. */}
                <th style={{ width: '38%' }}>English (source)</th>
                <th style={{ width: '38%' }}>{locale?.name ?? 'Translation'}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((k) => (
                <tr key={k.key}>
                  <td><Chip tone="us">{categoryOf(k.key)}</Chip></td>
                  <td><code className="mk-nowrap">{k.key}</code></td>
                  <td>{k.english}</td>
                  <td>
                    <input
                      className="mk-fld"
                      lang={chosen}
                      dir={locale?.direction ?? 'ltr'}
                      value={valueFor(k.key)}
                      /* The English, so an empty field previews what the customer
                         actually gets if it stays empty — which is the English,
                         not a blank. */
                      placeholder={k.english}
                      aria-label={k.key}
                      onChange={(e) => setDraft({ ...draft, [k.key]: e.target.value })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {rows.length === 0 ? (
        <Note>Nothing matches. Clear the filter, or the whole category is translated.</Note>
      ) : (
        <Note>
          Clearing a field deletes the translation rather than storing an empty one, so the key
          falls back to English. That fallback is what makes a partly-translated language safe to
          publish.
        </Note>
      )}
    </>
  );
}
